import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';

import AlertTriangleRed from '@/assets/icons/alertTriangleRed';
import { LineSeparator } from '@/components/LineSeparator';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useSbkEntrySummaryModal } from '@/feature/betslip-sbk/components/entry-summary-modal';
import { useIsBetSlipSubmitting } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { useShowPositionSummary } from '@/feature/betslip-sbk/hooks/use-show-position-summary';
import {
    activeSingleBetsSelector,
    hasActiveParlaySelector,
    hasStakeErrorSelector,
    totalBetCountSelector,
    totalPayoutSelector,
} from '@/feature/betslip-sbk/utils/betslip-selectors';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useIsCashCurrency } from '@/hooks/use-social-currency';

export const OneLineSummary = () => {
    const { t } = useTranslation(['betslip_sbk']);
    const { showEntrySummaryModal } = useSbkEntrySummaryModal();
    const showPositionSummary = useShowPositionSummary();
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const { formatCurrency } = useFormatCurrency();
    const isCashCurrency = useIsCashCurrency();
    const isPredictions = useIsPredictionsProduct();
    const hasStakeError = useSbkBetSlipStore(hasStakeErrorSelector);
    const activeSingleCount = useSbkBetSlipStore(state => activeSingleBetsSelector(state).length);
    const hasParlay = useSbkBetSlipStore(hasActiveParlaySelector);
    const totalPayout = useSbkBetSlipStore(totalPayoutSelector(isCashCurrency));
    const totalBetCount = useSbkBetSlipStore(totalBetCountSelector);
    const hasBetsWithStake = totalBetCount > 0;

    const showBetSummaryModal = () => {
        GASbkEvents.clickBetSummary();
        if (isPredictions) {
            showPositionSummary();
        } else {
            showEntrySummaryModal();
        }
    };

    const summary = useMemo(() => {
        if (isPredictions) {
            return t('single_position');
        }
        const parlay = hasParlay ? t('parlay_summary') : '';
        const singles = activeSingleCount > 0 ? t('single_with_count', { count: activeSingleCount }) : '';
        if (parlay && singles) {
            return t('summary_parlay_and_singles', { parlay, singles });
        }
        return parlay || singles;
    }, [isPredictions, hasParlay, activeSingleCount, t]);

    const textColor = hasStakeError ? ds.colorToken.text.danger : ('neutral300' as const);

    return (
        <Row justifyContent="center" alignItems="center">
            <TouchableOpacity onPress={showBetSummaryModal} disabled={isBetSlipSubmitting || !hasBetsWithStake}>
                <Row gap="s6">
                    {hasStakeError ? (
                        <Box top={2}>
                            <AlertTriangleRed />
                        </Box>
                    ) : null}
                    <Row>
                        {hasBetsWithStake ? (
                            <>
                                <Row gap="s4">
                                    <Text variant="labelLarge" letterSpacing={-0.08} color={textColor}>
                                        {t('summary_pays', { summary })}
                                    </Text>
                                    {isPredictions ? null : (
                                        <Box top={2}>
                                            <SocialCurrencyActiveIcon />
                                        </Box>
                                    )}
                                    <Text variant="labelLarge" letterSpacing={-0.08} color={textColor}>
                                        {formatCurrency(totalPayout)}
                                    </Text>
                                </Row>
                                <LineSeparator style={styles.separator} variant={hasStakeError ? 'error' : 'neutral'} />
                            </>
                        ) : (
                            <Text variant="labelLarge" letterSpacing={-0.08} color="neutral400">
                                {t(isPredictions ? 'add_buy_amount' : 'add_entry_amount')}
                            </Text>
                        )}
                    </Row>
                </Row>
            </TouchableOpacity>
        </Row>
    );
};

const styles = StyleSheet.create({
    separator: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
    },
});
